// Draw on a pending image before sending (8 Oct 2026). Julian: "screenshot
// something, attach it ... an edit button where I could annotate it, like
// circle. I wouldn't need text."
//
// Phone: a full-screen view with safe areas. Laptop: a large centred dialog.
// Freehand pen only (pointer events: touch, pen, mouse), four colours, two
// widths, Undo / Clear, Cancel / Done. Strokes live in IMAGE pixels, so they
// line up with the finger at any fit or zoom and flatten 1:1 on Done. The
// screen canvas is backed at devicePixelRatio so lines stay crisp.
//
// Done flattens image + strokes into a File (JPEG stays JPEG at 0.92, other
// types become PNG) and hands it to onSave; the composer swaps it in through
// the same compress + upload path as a new attachment. Cancel and Esc change
// nothing (Ops: no Back layer, see below).

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { AttachItem } from "../lib/types";
import "../styles/image-annotator.css";

const ANNOTATE_COLORS = [
  { name: "Red", value: "#ff3b30" },
  { name: "Yellow", value: "#ffcc00" },
  { name: "White", value: "#ffffff" },
  { name: "Black", value: "#111113" },
] as const;
/** Line widths in screen (CSS) pixels; stored per stroke in image pixels. */
const ANNOTATE_WIDTHS = [
  { name: "Thin line", css: 4, dot: 6 },
  { name: "Thick line", css: 9, dot: 12 },
] as const;
/** Longest edge of the flattened picture (and of the drawing space). */
const MAX_EDGE = 4096;

interface Point { x: number; y: number }
interface Stroke { color: string; width: number; points: Point[] }

/** Smooth path: quadratic curves through the midpoints of the raw samples. */
function drawStroke(ctx: CanvasRenderingContext2D, s: Stroke, scale: number) {
  const p = s.points;
  if (!p.length) return;
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  ctx.lineWidth = s.width * scale;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (p.length === 1) {
    ctx.beginPath();
    ctx.arc(p[0].x * scale, p[0].y * scale, (s.width * scale) / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.beginPath();
  ctx.moveTo(p[0].x * scale, p[0].y * scale);
  if (p.length === 2) {
    ctx.lineTo(p[1].x * scale, p[1].y * scale);
  } else {
    for (let i = 1; i < p.length - 1; i++) {
      const mx = (p[i].x + p[i + 1].x) / 2, my = (p[i].y + p[i + 1].y) / 2;
      ctx.quadraticCurveTo(p[i].x * scale, p[i].y * scale, mx * scale, my * scale);
    }
    const last = p[p.length - 1];
    ctx.lineTo(last.x * scale, last.y * scale);
  }
  ctx.stroke();
}

function annotatedName(filename: string, ext: string): string {
  const base = filename.replace(/\.[^.]+$/, "").replace(/-annotated$/, "");
  return `${base || "image"}-annotated.${ext}`;
}

/** Inline Lucide icons (stroke 1.75, currentColor). */
function Icon({ d, size = 20 }: { d: string[]; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.75"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {d.map((path) => <path key={path} d={path} />)}
    </svg>
  );
}
const UNDO = ["M9 14 4 9l5-5", "M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11"];
const CLEAR = ["M3 6h18", "M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6", "M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"];
const X = ["M18 6 6 18", "m6 6 12 12"];
const CHECK = ["M20 6 9 17l-5-5"];

interface Props {
  item: AttachItem;
  onCancel: () => void;
  /** Called with the flattened picture; the dialog closes right after. */
  onSave: (file: File) => void;
}

export default function ImageAnnotator({ item, onCancel, onSave }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const srcTypeRef = useRef<string>("image/jpeg");
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [loadFailed, setFailed] = useState(false);
  const failed = loadFailed || !item.blobUrl;
  const [fit, setFit] = useState<{ w: number; h: number } | null>(null);
  // History of stroke lists: Undo pops, Clear pushes [] (so Clear can be undone).
  const [history, setHistory] = useState<Stroke[][]>([[]]);
  const strokes = history[history.length - 1];
  const [color, setColor] = useState<string>(ANNOTATE_COLORS[0].value);
  const [widthIdx, setWidthIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const drawingRef = useRef<{ id: number; stroke: Stroke } | null>(null);
  const frameRef = useRef(0);
  const strokesRef = useRef(strokes);
  useLayoutEffect(() => { strokesRef.current = strokes; }, [strokes]);

  // Ops has no history layers (a Back step re-resolves the chat link there), so Esc and Cancel
  // close this view; Composer closes it when the chat changes.

  // Load the pending preview and learn its real type and size.
  useEffect(() => {
    let alive = true;
    const src = item.blobUrl;
    if (!src) return;
    void fetch(src).then((r) => r.blob()).then((b) => { if (b.type) srcTypeRef.current = b.type; }).catch(() => {});
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      if (!alive) return;
      const k = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
      imgRef.current = img;
      setNatural({ w: Math.max(1, Math.round(img.naturalWidth * k)), h: Math.max(1, Math.round(img.naturalHeight * k)) });
    };
    img.onerror = () => { if (alive) setFailed(true); };
    img.src = src;
    return () => { alive = false; };
  }, [item.blobUrl]);

  // Fit the picture inside the stage (contain), on open and on every resize/rotate.
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage || !natural) return;
    const measure = () => {
      const cs = getComputedStyle(stage);
      const aw = stage.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const ah = stage.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      if (aw <= 0 || ah <= 0) return;
      const k = Math.min(aw / natural.w, ah / natural.h);
      const w = Math.max(1, Math.floor(natural.w * k)), h = Math.max(1, Math.floor(natural.h * k));
      setFit((f) => (f && f.w === w && f.h === h ? f : { w, h }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [natural]);

  const paint = useCallback(() => {
    frameRef.current = 0;
    const canvas = canvasRef.current;
    if (!canvas || !natural) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const scale = canvas.width / natural.w;
    for (const s of strokesRef.current) drawStroke(ctx, s, scale);
    if (drawingRef.current) drawStroke(ctx, drawingRef.current.stroke, scale);
  }, [natural]);
  const schedulePaint = useCallback(() => {
    if (!frameRef.current) frameRef.current = requestAnimationFrame(paint);
  }, [paint]);
  useEffect(() => () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); }, []);

  // Back the screen canvas at devicePixelRatio for crisp lines; repaint on any change.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !fit) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const w = Math.round(fit.w * dpr), h = Math.round(fit.h * dpr);
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    paint();
  }, [fit, strokes, paint]);

  // Lock page scroll / iOS bounce while open; return focus to the pencil on close.
  useEffect(() => {
    const root = document.documentElement;
    const back = document.activeElement as HTMLElement | null;
    root.classList.add("annot-open");
    const panel = rootRef.current?.querySelector<HTMLElement>(".annot__panel");
    panel?.focus({ preventScroll: true });
    const el = rootRef.current;
    const stopScroll = (e: TouchEvent) => { if (e.cancelable) e.preventDefault(); };
    el?.addEventListener("touchmove", stopScroll, { passive: false });
    return () => {
      root.classList.remove("annot-open");
      el?.removeEventListener("touchmove", stopScroll);
      if (back?.isConnected) back.focus({ preventScroll: true });
    };
  }, []);

  const undo = useCallback(() => setHistory((h) => (h.length > 1 ? h.slice(0, -1) : h)), []);
  const clear = useCallback(() => setHistory((h) => (h[h.length - 1].length ? [...h, []] : h)), []);

  const done = useCallback(async () => {
    if (saving) return;
    const img = imgRef.current;
    const list = strokesRef.current;
    if (!img || !natural || !list.length) { onCancel(); return; }
    setSaving(true);
    try {
      const out = document.createElement("canvas");
      out.width = natural.w;
      out.height = natural.h;
      const ctx = out.getContext("2d");
      if (!ctx) throw new Error("no canvas");
      const jpeg = srcTypeRef.current === "image/jpeg";
      if (jpeg) { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, out.width, out.height); }
      ctx.drawImage(img, 0, 0, out.width, out.height);
      for (const s of list) drawStroke(ctx, s, 1);
      const type = jpeg ? "image/jpeg" : "image/png";
      const blob = await new Promise<Blob | null>((res) => out.toBlob(res, type, jpeg ? 0.92 : undefined));
      if (!blob) throw new Error("toBlob");
      onSave(new File([blob], annotatedName(item.filename, jpeg ? "jpg" : "png"), { type }));
    } catch {
      setSaving(false);
      onCancel();
    }
  }, [saving, natural, onCancel, onSave, item.filename]);

  // Keys: Esc cancels, Cmd/Ctrl+Z undoes, Tab stays inside.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onCancel(); return; }
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault(); e.stopPropagation(); undo(); return;
      }
      if (e.key === "Tab") {
        const items = Array.from(rootRef.current?.querySelectorAll<HTMLElement>("button:not(:disabled)") ?? []);
        if (!items.length) return;
        const i = items.indexOf(document.activeElement as HTMLElement);
        const next = e.shiftKey ? (i <= 0 ? items.length - 1 : i - 1) : (i + 1) % items.length;
        e.preventDefault();
        items[next].focus();
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onCancel, undo]);

  // Pointer drawing (one pointer at a time; extra fingers are ignored).
  const toImage = (canvas: HTMLCanvasElement, e: { clientX: number; clientY: number }): Point => {
    const r = canvas.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(natural!.w, ((e.clientX - r.left) / r.width) * natural!.w)),
      y: Math.max(0, Math.min(natural!.h, ((e.clientY - r.top) / r.height) * natural!.h)),
    };
  };
  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!natural || drawingRef.current || saving) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    const canvas = e.currentTarget;
    try { canvas.setPointerCapture(e.pointerId); } catch { /* synthetic events */ }
    const r = canvas.getBoundingClientRect();
    const perCss = natural.w / r.width;
    drawingRef.current = {
      id: e.pointerId,
      stroke: { color, width: ANNOTATE_WIDTHS[widthIdx].css * perCss, points: [toImage(canvas, e)] },
    };
    schedulePaint();
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drawingRef.current;
    if (!d || d.id !== e.pointerId || !natural) return;
    e.preventDefault();
    const canvas = e.currentTarget;
    const r = canvas.getBoundingClientRect();
    const minGap = (natural.w / r.width) * 1.5; // skip jitter under ~1.5 screen px
    const native = e.nativeEvent;
    const samples = typeof native.getCoalescedEvents === "function" ? native.getCoalescedEvents() : [];
    for (const ev of samples.length ? samples : [native]) {
      const p = toImage(canvas, ev);
      const last = d.stroke.points[d.stroke.points.length - 1];
      if (Math.hypot(p.x - last.x, p.y - last.y) < minGap) continue;
      d.stroke.points.push(p);
    }
    schedulePaint();
  };
  const finish = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drawingRef.current;
    if (!d || d.id !== e.pointerId) return;
    drawingRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    const stroke = { ...d.stroke, points: d.stroke.points.slice() };
    setHistory((h) => [...h, [...h[h.length - 1], stroke]]);
  };

  const hasStrokes = strokes.length > 0;
  return createPortal(
    <div ref={rootRef} className="annot" role="dialog" aria-modal="true" aria-labelledby="annot-title" data-no-swipe>
      <div className="annot__panel" tabIndex={-1}>
        <header className="annot__bar">
          <button type="button" className="annot__btn annot__btn--ghost" onClick={onCancel}>
            <Icon d={X} /><span>Cancel</span>
          </button>
          <h2 className="annot__title" id="annot-title">Draw on image</h2>
          <button type="button" className="annot__btn annot__btn--primary" onClick={() => void done()}
            disabled={saving || !natural} aria-busy={saving || undefined}>
            <Icon d={CHECK} /><span>{saving ? "Saving…" : "Done"}</span>
          </button>
        </header>

        <div className="annot__stage" ref={stageRef}>
          {failed ? (
            <p className="annot__error">This image couldn't be opened. Cancel and attach it again.</p>
          ) : (
            <div className="annot__frame" style={fit ? { width: fit.w, height: fit.h } : { visibility: "hidden" }}>
              {item.blobUrl && <img className="annot__img" src={item.blobUrl} alt="" draggable={false} />}
              <canvas
                ref={canvasRef}
                className="annot__canvas"
                aria-label="Drawing area"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={finish}
                onPointerCancel={finish}
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
          )}
        </div>

        <footer className="annot__tools" role="toolbar" aria-label="Pen">
          <div className="annot__group" role="radiogroup" aria-label="Colour">
            {ANNOTATE_COLORS.map((c) => (
              <button key={c.value} type="button" role="radio" aria-checked={color === c.value} aria-label={c.name}
                title={c.name} className={`annot__tool annot__swatch ${color === c.value ? "is-on" : ""}`}
                onClick={() => setColor(c.value)}>
                <span className="annot__dot" style={{ background: c.value }} />
              </button>
            ))}
          </div>
          <span className="annot__sep" aria-hidden="true" />
          <div className="annot__group" role="radiogroup" aria-label="Line width">
            {ANNOTATE_WIDTHS.map((w, i) => (
              <button key={w.name} type="button" role="radio" aria-checked={widthIdx === i} aria-label={w.name}
                title={w.name} className={`annot__tool annot__width ${widthIdx === i ? "is-on" : ""}`}
                onClick={() => setWidthIdx(i)}>
                <span className="annot__dot" style={{ width: w.dot, height: w.dot, background: color }} />
              </button>
            ))}
          </div>
          <span className="annot__sep" aria-hidden="true" />
          <div className="annot__group">
            <button type="button" className="annot__tool" onClick={undo} disabled={history.length < 2}
              aria-label="Undo" title="Undo">
              <Icon d={UNDO} />
            </button>
            <button type="button" className="annot__tool" onClick={clear} disabled={!hasStrokes}
              aria-label="Clear drawing" title="Clear drawing">
              <Icon d={CLEAR} />
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
